import {
  Box,
  Button,
  NormalButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@crayon/design-system-react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Product,
  ProductMappingMigrationModel,
  ProgramType,
  Source,
  Target,
  TargetProductType,
} from "api/client.generated";
import { ValidationException } from "api/client.generated.extensions";
import FormAutocomplete from "components/primitives/FormAutocomplete";
import FormCheckbox from "components/primitives/FormCheckbox";
import FormErrorMessage from "components/primitives/FormErrorMessage";
import TableBodyCell from "components/primitives/TableBodyCell";
import TableBodySkeleton from "components/primitives/TableBodySkeleton";
import TableHeaderCell from "components/primitives/TableHeaderCell";
import useSourcePartnerProductMappings from "hooks/api/PartnerMigration/useSourcePartnerProductMappings";
import useApi from "hooks/api/useApi";
import useSourceProducts from "hooks/api/useSourceProducts";
import { ChecklistIcon } from "images/MuiIcons";
import { useEffect, useMemo, useState } from "react";
import { Path, useFieldArray, useForm, useWatch } from "react-hook-form";
import DefaultPaginationOptions from "types/defaultPaginationOptions";
import NotificationMessage from "types/notification-message";
import TableColumnInfo from "types/table-column-info";
import * as yup from "yup";
import { useAuthorizedTargetsContext } from "../AuthorizedTargetsContext";
import MigratePartnerStepProps from "../MigratePartnerStepProps";
import MigrationFormActionButtons from "../MigrationFormActionButtons";
import WarningIconWithTooltip, { TooltipMessage } from "../WarningIconWithTooltip";

const arrayItemSchema = yup.object().shape({
  checked: yup.boolean(),
  target: yup.string().oneOf(Object.values(Target)).required(),
  targetType: yup.string().oneOf(Object.values(TargetProductType)).required(),
  mappingId: yup.number().required(),
  // should align with Product type from 'api/client.generated'
  ciqProduct: yup
    .object()
    .shape({
      id: yup.string().required(),
      name: yup.string().required(),
      sku: yup.string().required(),
    })
    .nullable()
    .when("checked", {
      is: (checked: boolean) => checked,
      then: (schema) => schema.required("Required"),
    }),

  // auxiliary fields for label and logic purposes
  nameInPrism: yup.string().required(),
  nameInPsa: yup.string().required(),
  sku: yup.string().required(),
  offeringTerm: yup.string().required(),
  isAutoMatched: yup.boolean().required(),
  fieldIndex: yup.number().required(),
  isTargetAuthorized: yup.boolean(),
});

const formSchema = yup.object().shape({
  items: yup.array().of(arrayItemSchema),
});

type SchemaType = yup.InferType<typeof formSchema>;
type SchemaItemType = yup.InferType<typeof arrayItemSchema>;

const ProductMappingsStep = ({ onStepComplete }: MigratePartnerStepProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPageOptions = useMemo<number[]>(() => DefaultPaginationOptions, []);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [hideSwitch, setHideSwitch] = useState(false);
  const [formErrorMsg, setFormErrorMsg] = useState("");
  const { partnersMigrationClient } = useApi();

  const { productMappings, isProductMappingsFetching } = useSourcePartnerProductMappings();
  const { authorizedTargets } = useAuthorizedTargetsContext();

  const { products: ciqProducts, isProductsFetching: isCiqProductsFetching } = useSourceProducts(
    Source.CloudIq,
    ProgramType.MicrosoftCsp,
  );

  const { control, handleSubmit, clearErrors } = useForm<SchemaType>({
    resolver: yupResolver(formSchema),
    defaultValues: { items: [] },
  });

  const { fields, append, remove, replace, update } = useFieldArray({
    control,
    name: "items",
    keyName: "fieldId",
  });

  const fieldsWatch = useWatch({ control, name: "items" });

  // to clear validation errors when on item unselect action
  useEffect(() => {
    fieldsWatch
      ?.filter((x) => !x.checked)
      .forEach((x) => {
        clearErrors(`items.${x.fieldIndex}.ciqProduct`);
      });
  }, [fieldsWatch, clearErrors]);

  // init when products mappings are fetched
  useEffect(() => {
    if (isProductMappingsFetching || !productMappings || fields.length > 0) return;

    // if rerender, clean up fields to avoid duplication
    if (fields.length > 0) remove([...Array(fields.length).keys()]);

    productMappings
      .sort((a, b) => {
        if (a.target > b.target) return 1;
        if (a.target < b.target) return -1;

        if (a.sourceName > b.sourceName) return 1;
        return a.sourceName < b.sourceName ? -1 : 0;
      })
      .forEach((x, i) => {
        append({
          checked: false,
          target: x.target,
          targetType: x.targetType,
          ciqProduct: null,
          mappingId: x.mappingId,
          nameInPrism: x.sourceName,
          nameInPsa: x.targetName,
          sku: x.sku,
          offeringTerm: x.offeringTerm,
          isAutoMatched: false,
          fieldIndex: i,
          isTargetAuthorized: authorizedTargets.some((t) => t === x.target),
        });
      });
  }, [
    isProductMappingsFetching,
    productMappings,
    append,
    remove,
    fields.length,
    authorizedTargets,
  ]);

  const tableFields = useMemo(() => {
    const controlledFields = fields.map((field, index) => ({
      ...field,
      ...fieldsWatch![index],
    }));

    return controlledFields.filter((x) => (hideSwitch ? !x.checked || !x.isAutoMatched : true));
  }, [fields, fieldsWatch, hideSwitch]);

  const columnOptions = useMemo<TableColumnInfo[]>(
    () => [
      { label: "" },
      { label: "PSA" },
      { label: "Prism Name" },
      { label: "SKU" },
      { label: "Term" },
      { label: "Name in PSA" },
      { label: "Cloud-iQ SKU", width: "20%" },
      { label: "Cloud-iQ Name", width: "20%" },
    ],
    [],
  );

  const onAutocompleteChange = (
    schemaFieldName: Path<SchemaType>,
    newValue: Product | null,
    oldValue: Product | null,
  ) => {
    const parts = schemaFieldName.split(".");
    if (parts.length !== 3) return;

    const index: number = +parts[1];
    if (!fieldsWatch || !Number.isInteger(index)) return;
    if (!fieldsWatch[index].isAutoMatched || newValue?.id === oldValue?.id) return;

    update(index, { ...fieldsWatch[index], isAutoMatched: false });
  };

  const onAutoMatch = () => {
    if (!fieldsWatch || !ciqProducts) return;

    const ciqMap = new Map<string, Product>();
    for (let i = 0; i < ciqProducts.length; i += 1) {
      ciqMap.set(ciqProducts[i].sku, ciqProducts[i]);
    }

    const finalResult: SchemaItemType[] = [];
    const prodMap = new Map<string, SchemaItemType[]>();

    for (let i = 0; i < fieldsWatch.length; i += 1) {
      if (!fieldsWatch[i].checked && fieldsWatch[i].isTargetAuthorized) {
        let vals = prodMap.get(fieldsWatch[i].sku);
        if (vals) {
          vals = [...vals, fieldsWatch[i]];
        } else {
          vals = [fieldsWatch[i]];
        }
        prodMap.set(fieldsWatch[i].sku, vals);
      } else {
        finalResult.push(fieldsWatch[i]);
      }
    }

    prodMap.forEach((v, k) => {
      const ciqMatch = ciqMap.get(k);
      for (let i = 0; i < v.length; i += 1) {
        finalResult.push({
          ...v[i],
          ...(ciqMatch && {
            checked: true,
            isAutoMatched: true,
            ciqProduct: {
              id: ciqMatch.id,
              name: ciqMatch.name,
              sku: ciqMatch.sku,
            },
          }),
        });
      }
    });

    replace(
      finalResult.sort((a, b) => {
        if (a.fieldIndex > b.fieldIndex) return 1;
        return a.fieldIndex < b.fieldIndex ? -1 : 0;
      }),
    );
  };

  const onResetAutoMatch = () => {
    if (!fieldsWatch) return;

    replace(
      fieldsWatch.map((x) => ({
        ...x,
        ...(x.isAutoMatched && { ciqProduct: null, isAutoMatched: false, checked: false }),
      })),
    );

    setHideSwitch(false);
  };

  const onSave = async (formData: SchemaType) => {
    try {
      setIsSaving(true);

      const migrations: ProductMappingMigrationModel[] =
        formData.items
          ?.filter((x) => x.checked)
          .map((x) => ({
            mappingId: x.mappingId,
            target: x.target,
            targetType: x.targetType,
            cloudIqProductId: x.ciqProduct!.id,
            cloudIqProductSku: x.ciqProduct!.sku,
          })) ?? [];

      await partnersMigrationClient.migrateProductMappings(migrations);

      onStepComplete();
    } catch (e: unknown) {
      const validationError = ValidationException.parse(e);
      if (validationError) {
        setFormErrorMsg(validationError.getFirstErrorMessage());
      } else {
        setFormErrorMsg(NotificationMessage.UNKNOWN_ERROR);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const AutoMatchSlice = (
    <Box display="flex" flexDirection="row" justifyContent="space-between">
      <Box display="flex" flexDirection="row" gap={2}>
        <NormalButton
          onClick={() => onAutoMatch()}
          disabled={isSaving || isProductMappingsFetching || isCiqProductsFetching}
          testId="auto-match-button"
        >
          Auto match
        </NormalButton>
        <Button
          color="error"
          variant="outlined"
          onClick={onResetAutoMatch}
          disabled={isSaving || isProductMappingsFetching || isCiqProductsFetching}
          testId="reset-auto-match-button"
        >
          Reset auto matched items
        </Button>
      </Box>
      <Box display="flex" flexDirection="row" alignItems="center">
        <Switch
          checked={hideSwitch}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHideSwitch(e.target.checked)}
          disabled={isSaving || isProductMappingsFetching || isCiqProductsFetching}
          testId="hide-auto-matched-items-switch"
        />
        <Typography variant="body1">Hide auto matched items</Typography>
      </Box>
    </Box>
  );

  const DataTableHead = (
    <TableHead>
      <TableRow>
        <TableCell size="small" sx={{ width: "45px" }}>
          <Box display="flex">
            <ChecklistIcon />
          </Box>
        </TableCell>
        {columnOptions.slice(1).map((x, i) => (
          <TableHeaderCell key={x.label + String(i)} columnName={x.label} width={x.width} />
        ))}
      </TableRow>
    </TableHead>
  );

  const DataTableBody = (
    <TableBody>
      {tableFields.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((x) => (
        <TableRow key={x.fieldId}>
          <TableCell>
            <FormCheckbox
              control={control}
              bindSchemaFieldName={`items.${x.fieldIndex}.checked`}
              label=""
              disabled={
                isSaving ||
                isProductMappingsFetching ||
                isCiqProductsFetching ||
                !x.isTargetAuthorized
              }
            />
          </TableCell>
          <TableBodyCell
            label={x.target}
            {...(!x.isTargetAuthorized && {
              icon: <WarningIconWithTooltip message={TooltipMessage.PsaNotAuthorized} />,
            })}
          />
          <TableBodyCell label={x.nameInPrism} />
          <TableBodyCell label={x.sku} />
          <TableBodyCell label={x.offeringTerm} />
          <TableBodyCell label={x.nameInPsa} />
          <TableCell>
            <Box display="flex">
              <FormAutocomplete
                control={control}
                bindSchemaFieldName={`items.${x.fieldIndex}.ciqProduct`}
                label="Cloud-iQ SKU"
                disabled={!fieldsWatch?.at(x.fieldIndex)?.checked || isSaving}
                options={ciqProducts ?? []}
                sxRoot={{ flex: 1 }}
                clearable
                testId="ciq-product-sku-autocomplete"
                getOptionLabel={(item: Product) => item.sku}
                isLoading={isCiqProductsFetching}
                onChangeValue={onAutocompleteChange}
              />
            </Box>
          </TableCell>
          <TableCell>
            <Box display="flex">
              <FormAutocomplete
                control={control}
                bindSchemaFieldName={`items.${x.fieldIndex}.ciqProduct`}
                label="Cloud-iQ Name"
                disabled={!fieldsWatch?.at(x.fieldIndex)?.checked || isSaving}
                options={ciqProducts ?? []}
                sxRoot={{ flex: 1 }}
                clearable
                testId="ciq-product-name-autocomplete"
                getOptionLabel={(item: Product) => item.name}
                isLoading={isCiqProductsFetching}
                onChangeValue={onAutocompleteChange}
              />
            </Box>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );

  return (
    <form onSubmit={handleSubmit(onSave)}>
      <Box display="flex" flexDirection="column" gap={1}>
        {AutoMatchSlice}
        <TableContainer component={Paper}>
          <Table testId="migrate-product-mappings-test-id">
            {DataTableHead}
            {isProductMappingsFetching ? (
              <TableBodySkeleton colsCount={columnOptions.length} rowsCount={3} />
            ) : (
              DataTableBody
            )}
          </Table>
          <TablePagination
            component={Box}
            count={tableFields.length}
            page={page}
            rowsPerPageOptions={rowsPerPageOptions}
            rowsPerPage={rowsPerPage}
            onPageChange={(_: unknown, newPage: number) => setPage(newPage)}
            onRowsPerPageChange={(
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            sx={{ my: 1 }}
          />
        </TableContainer>
        <FormErrorMessage message={formErrorMsg} />
        <MigrationFormActionButtons
          onSkipClick={onStepComplete}
          isSaveDisable={!fieldsWatch?.some((x) => x.checked)}
          isSaveLoading={isSaving}
        />
      </Box>
    </form>
  );
};

export default ProductMappingsStep;
